import React, { useContext, useEffect, useMemo, useState } from "react";
import { AiFillCloseCircle } from "react-icons/ai";
import {
  SiFacebook,
  SiInstagram,
  SiTiktok,
  SiLinkedin,
  SiPinterest,
  SiYoutube,
} from "react-icons/si";
import { AlertContext, UserContext } from "../context/MainContext";
import { userUpdate } from "../actions/userUpdate";
import InputField, { TextareaField } from "./InputField";
const ModalEdit = ({ modal, setModal }) => {
  const { user, setUser } = useContext(UserContext);
  const { setAlert } = useContext(AlertContext);
  const [form, setForm] = useState({});
  const [initialForm, setInitialForm] = useState({});

  const normalizeHandle = (val) => {
    if (!val) return val;
    return val.trim().replace(/^@+/, "");
  };

  const ensureScheme = (url) => {
    if (!url) return url;
    const v = url.trim();
    if (/^https?:\/\//i.test(v)) return v.replace(/^http:\/\//i, "https://");
    return `https://${v}`;
  };

  const normalizeSocial = (value, platform) => {
    if (!value) return value;
    const v = value.trim();
    // if already a full url, ensure https
    if (/^https?:\/\//i.test(v) || /^www\./i.test(v)) {
      return ensureScheme(v);
    }
    // if includes domain already without scheme
    if (
      v.match(
        /(instagram\.com|facebook\.com|twitter\.com|x\.com|tiktok\.com|linkedin\.com|pinterest\.com|youtube\.com|youtu\.be)/i,
      )
    ) {
      return ensureScheme(v);
    }
    const handle = normalizeHandle(v);
    switch (platform) {
      case "instagram":
        return `https://www.instagram.com/${handle}`;
      case "facebook":
        return `https://www.facebook.com/${handle}`;
      case "twitter":
      case "x":
        return `https://x.com/${handle}`;
      case "tiktok":
        return `https://www.tiktok.com/@${handle}`;
      case "linkedin":
        return `https://www.linkedin.com/in/${handle}`;
      case "pinterest":
        return `https://www.pinterest.com/${handle}`;
      case "youtube":
        return `https://www.youtube.com/@${handle}`;
      default:
        return value;
    }
  };

  useEffect(() => {
    // initialize local form when modal opens
    const snapshot = user ? { ...user } : {};
    setForm(snapshot);
    setInitialForm(snapshot);
  }, [modal, user]);

  const isDirty = useMemo(() => {
    const keys = new Set([...Object.keys(initialForm), ...Object.keys(form)]);
    for (const key of keys) {
      if (String(form?.[key] || "") !== String(initialForm?.[key] || "")) {
        return true;
      }
    }
    return false;
  }, [form, initialForm]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const isSupportedVideoUrl = (val) => {
    if (!val) return true;
    const safe = ensureScheme(val);
    // Check if it's a valid URL format
    try {
      new URL(safe);
      return true;
    } catch {
      return false;
    }
  };

  const isValidPhoneNumber = (val) => {
    if (!val) return true;
    // Simple validation: at least 10 digits
    const digitsOnly = val.replace(/\D/g, "");
    return digitsOnly.length >= 10;
  };

  const isValidDescription = (val) => {
    if (!val) return true;
    // At least 10 characters
    return val.trim().length >= 10;
  };

  const isValidSocialUrl = (val, platform) => {
    if (!val) return true;
    const normalized = normalizeSocial(val, platform);
    try {
      new URL(normalized);
      return true;
    } catch {
      return false;
    }
  };

  const handleUpdate = async () => {
    // normalize social links before sending
    const normalized = { ...form };
    normalized.facebook = normalizeSocial(form?.facebook, "facebook");
    normalized.instagram = normalizeSocial(form?.instagram, "instagram");
    // preserve twitter field name used by backend
    normalized.twitter = normalizeSocial(form?.twitter, "x");
    normalized.tiktok = normalizeSocial(form?.tiktok, "tiktok");
    normalized.linkedin = normalizeSocial(form?.linkedin, "linkedin");
    normalized.pinterest = normalizeSocial(form?.pinterest, "pinterest");
    normalized.youtube = normalizeSocial(form?.youtube, "youtube");

    // Normalize all 4 video URLs
    for (let i = 1; i <= 4; i++) {
      const videoKey = i === 1 ? "video" : `video${i}`;
      if (form?.[videoKey] && !/^https?:\/\//i.test(form[videoKey])) {
        normalized[videoKey] = ensureScheme(form[videoKey]);
      }
    }

    // Validate all videos
    for (let i = 1; i <= 4; i++) {
      const videoKey = i === 1 ? "video" : `video${i}`;
      if (normalized[videoKey] && !isSupportedVideoUrl(normalized[videoKey])) {
        setAlert([
          "Invalid video URL",
          `Video ${i}: Please enter a valid URL (must start with http:// or https://)`,
          "failed",
        ]);
        return;
      }
    }

    // build a minimal changes object: only send fields the user actually modified
    const changes = {};
    Object.keys(normalized).forEach((k) => {
      // skip undefined
      const newVal = normalized[k];
      const oldVal = user?.[k];
      // treat null/undefined and empty strings distinctly; consider changed when different
      const changed = (() => {
        if (typeof newVal === "undefined" && typeof oldVal === "undefined")
          return false;
        // simple deep-compare for primitives
        return String(newVal || "") !== String(oldVal || "");
      })();
      if (changed) changes[k] = newVal;
    });

    // ensure we include the user identifier so server can apply update
    if (Object.keys(changes).length > 0) {
      changes.user_id = user.user_id;
      const ok = await userUpdate(changes, setUser);
      if (ok) {
        setInitialForm({ ...form, ...changes });
        setAlert([
          "Profile updated",
          "Your profile changes have been saved successfully.",
          "confirm",
        ]);
      } else {
        setAlert([
          "Update failed",
          "We could not save your changes. Please try again.",
          "failed",
        ]);
        return;
      }
    }
    setModal(false);
  };

  const handleClose = () => {
    if (isDirty) {
      const leave = window.confirm(
        "You have unsaved changes. Leave without saving?",
      );
      if (!leave) return;
    }
    setModal(false);
  };
  return (
    <>
      <div className=" flex bg-black/40 w-[100%] top-0  left-0 h-screen fixed z-50 "></div>
      <div className="flex justify-center max-w-[1950px] top-32 absolute z-50 tablet:px-16 homebreak:px-32 w-full">
        <div className="bg-white flex flex-col p-4 pt-6 px-14 text-center relative w-[800px]">
          <div
            className="absolute right-4 hover:scale-105 hover:cursor-pointer transition-all"
            onClick={handleClose}
          >
            <AiFillCloseCircle size={30} />
          </div>
          <h1 className="mb-2 text-30">Edit Profile</h1>
          <hr />
          <p className="my-2 text-xs">
            Changes will only be made upon pressing UPDATE
          </p>
          <div className="flex flex-col gap-4">
            <div className="flex gap-4 flex-wrap">
              <div className="flex flex-col flex-[0.5]">
                <label className="text-left text-black">Legal First Name</label>
                <input
                  className="bg-slate-300 py-[10px] px-2 cursor-not-allowed"
                  name="legal_first_name"
                  value={form?.legal_first_name || ""}
                  readOnly
                  aria-readonly="true"
                  title="Legal name can only be set during registration"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Legal name can only be set during registration.
                </p>
              </div>
              <div className="flex flex-col flex-[0.5]">
                <label className="text-left text-black"> Legal Last Name</label>
                <input
                  className="bg-slate-300 py-[10px] px-2 cursor-not-allowed"
                  name="legal_last_name"
                  value={form?.legal_last_name || ""}
                  readOnly
                  aria-readonly="true"
                  title="Legal name can only be set during registration"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Legal name can only be set during registration.
                </p>
              </div>
            </div>
            {/* <div className="flex flex-col">
                        <label className="text-left text-19">Profession</label>
                        <input className="bg-slate-300"/>
                    </div>
                    <div className="flex flex-col">
                        <label className="text-left text-19">Profession ID</label>
                        <input className="bg-slate-300"/>
                    </div> */}
            <div className="flex flex-col">
              <label className="text-left text-19">About Description</label>
              <TextareaField
                className="bg-slate-300 px-4 py-2"
                name="about"
                value={form?.about || ""}
                onChange={(e) => handleChange(e)}
                label="About Description"
              />
              {form?.about && !isValidDescription(form?.about) && (
                <p className="text-red-600 text-sm mt-1">
                  ⚠️ Description should be at least 10 characters
                </p>
              )}
              {form?.about && isValidDescription(form?.about) && (
                <p className="text-green-600 text-sm mt-1">
                  ✓ Description looks good
                </p>
              )}
            </div>
            <div className="flex flex-col">
              <label className="text-left text-19">Phone Number</label>
              <input
                className="bg-slate-300 px-4 py-2"
                name="phone_number"
                value={form?.phone_number || ""}
                onChange={(e) => handleChange(e)}
              />
              {form?.phone_number &&
                !isValidPhoneNumber(form?.phone_number) && (
                  <p className="text-red-600 text-sm mt-1">
                    ⚠️ Phone number must have at least 10 digits
                  </p>
                )}
              {form?.phone_number && isValidPhoneNumber(form?.phone_number) && (
                <p className="text-green-600 text-sm mt-1">
                  ✓ Phone number looks good
                </p>
              )}
            </div>
            {user?.class_type === "Realtor" ||
            user?.class_type === "Investor" ||
            user?.class_type === "Lender" ? (
              <>
                <div className="flex flex-col">
                  <label className="text-left text-19">Brokerage Name</label>
                  <InputField
                    className="bg-slate-300 px-4 py-2"
                    name="brokerage_name"
                    value={form?.brokerage_name || ""}
                    onChange={(e) => handleChange(e)}
                    label="Brokerage Name"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-left text-19">Brokerage Number</label>
                  <InputField
                    className="bg-slate-300 px-4 py-2"
                    name="brokerage_number"
                    value={form?.brokerage_number || ""}
                    onChange={(e) => handleChange(e)}
                    label="Brokerage Number"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-left text-19">Broker Name</label>
                  <InputField
                    className="bg-slate-300 px-4 py-2"
                    name="broker_name"
                    value={form?.broker_name || ""}
                    onChange={(e) => handleChange(e)}
                    label="Broker Name"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-left text-19">Broker Number</label>
                  <InputField
                    className="bg-slate-300 px-4 py-2"
                    name="broker_number"
                    value={form?.broker_number || ""}
                    onChange={(e) => handleChange(e)}
                    label="Broker Number"
                  />
                </div>
                {user?.class_type === "Realtor" && (
                  <>
                    <div className="flex flex-col">
                      <label className="text-left text-19">NAR Member</label>
                      <select
                        className="bg-slate-300 px-4 py-2"
                        name="is_realtor"
                        value={form?.is_realtor ? "true" : "false"}
                        onChange={(e) =>
                          handleChange({
                            target: {
                              name: "is_realtor",
                              value: e.target.value === "true",
                            },
                          })
                        }
                      >
                        <option value="false">No</option>
                        <option value="true">Yes</option>
                      </select>
                    </div>
                    {form?.is_realtor && (
                      <div className="flex flex-col">
                        <label className="text-left text-19">
                          Broker/Managing Firm
                        </label>
                        <select
                          className="bg-slate-300 px-4 py-2"
                          name="is_broker"
                          value={form?.is_broker ? "true" : "false"}
                          onChange={(e) =>
                            handleChange({
                              target: {
                                name: "is_broker",
                                value: e.target.value === "true",
                              },
                            })
                          }
                        >
                          <option value="false">Agent</option>
                          <option value="true">Broker/Owner</option>
                        </select>
                      </div>
                    )}
                  </>
                )}
              </>
            ) : (
              ""
            )}
            {user?.class_type === "Builder" && (
              <>
                <div className="flex flex-col">
                  <label className="text-left text-19">
                    Builder Company Name
                  </label>
                  <InputField
                    className="bg-slate-300 px-4 py-2"
                    name="builder_company_name"
                    value={form?.builder_company_name || ""}
                    onChange={(e) => handleChange(e)}
                    label="Builder Company Name"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-left text-19">
                    Builder Company Supervisor
                  </label>
                  <InputField
                    className="bg-slate-300 px-4 py-2"
                    name="builder_company_supervisor"
                    value={form?.builder_company_supervisor || ""}
                    onChange={(e) => handleChange(e)}
                    label="Builder Company Supervisor"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-left text-19">
                    Direct Office Contact #
                  </label>
                  <InputField
                    className="bg-slate-300 px-4 py-2"
                    name="direct_office"
                    value={form?.direct_office || ""}
                    onChange={(e) => handleChange(e)}
                    label="Direct Office Contact"
                  />
                </div>
                <h3 className="text-left text-lg font-semibold text-slate-900 mt-4">
                  Phase 1: Builder Credentials
                </h3>
                <div className="flex flex-col">
                  <label className="text-left text-19">
                    Builder License Number
                  </label>
                  <InputField
                    className="bg-slate-300 px-4 py-2"
                    name="builder_number"
                    value={form?.builder_number || ""}
                    onChange={(e) => handleChange(e)}
                    label="Builder License Number"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-left text-19">Builder Email</label>
                  <InputField
                    className="bg-slate-300 px-4 py-2"
                    name="builder_email"
                    value={form?.builder_email || ""}
                    onChange={(e) => handleChange(e)}
                    label="Builder Email"
                    type="email"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-left text-19">Supervisor Name</label>
                  <InputField
                    className="bg-slate-300 px-4 py-2"
                    name="builder_supervisor_name"
                    value={form?.builder_supervisor_name || ""}
                    onChange={(e) => handleChange(e)}
                    label="Supervisor Name"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-left text-19">Supervisor Email</label>
                  <InputField
                    className="bg-slate-300 px-4 py-2"
                    name="builder_supervisor_email"
                    value={form?.builder_supervisor_email || ""}
                    onChange={(e) => handleChange(e)}
                    label="Supervisor Email"
                    type="email"
                  />
                </div>
              </>
            )}
            {user?.class_type === "Title Company" && (
              <>
                <h3 className="text-left text-lg font-semibold text-slate-900">
                  Title Company Information
                </h3>
                <div className="flex flex-col">
                  <label className="text-left text-19">Company Name</label>
                  <InputField
                    className="bg-slate-300 px-4 py-2"
                    name="title_company_name"
                    value={form?.title_company_name || ""}
                    onChange={(e) => handleChange(e)}
                    label="Title Company Name"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-left text-19">Company Address</label>
                  <InputField
                    className="bg-slate-300 px-4 py-2"
                    name="title_company_address"
                    value={form?.title_company_address || ""}
                    onChange={(e) => handleChange(e)}
                    label="Company Address"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-left text-19">Company Phone</label>
                  <InputField
                    className="bg-slate-300 px-4 py-2"
                    name="title_company_phone"
                    value={form?.title_company_phone || ""}
                    onChange={(e) => handleChange(e)}
                    label="Company Phone"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-left text-19">Supervisor Name</label>
                  <InputField
                    className="bg-slate-300 px-4 py-2"
                    name="title_company_supervisor_name"
                    value={form?.title_company_supervisor_name || ""}
                    onChange={(e) => handleChange(e)}
                    label="Supervisor Name"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-left text-19">Supervisor Email</label>
                  <InputField
                    className="bg-slate-300 px-4 py-2"
                    name="title_company_supervisor_email"
                    value={form?.title_company_supervisor_email || ""}
                    onChange={(e) => handleChange(e)}
                    label="Supervisor Email"
                    type="email"
                  />
                </div>
              </>
            )}
            {user?.class_type === "Lender" && (
              <>
                <h3 className="text-left text-lg font-semibold text-slate-900">
                  Lender Credentials
                </h3>
                <div className="flex flex-col">
                  <label className="text-left text-19">
                    Company License Number
                  </label>
                  <InputField
                    className="bg-slate-300 px-4 py-2"
                    name="company_license_number"
                    value={form?.company_license_number || ""}
                    onChange={(e) => handleChange(e)}
                    label="Company License Number"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-left text-19">Supervisor Name</label>
                  <InputField
                    className="bg-slate-300 px-4 py-2"
                    name="supervisor_name"
                    value={form?.supervisor_name || ""}
                    onChange={(e) => handleChange(e)}
                    label="Supervisor Name"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-left text-19">
                    Supervisor License Number
                  </label>
                  <InputField
                    className="bg-slate-300 px-4 py-2"
                    name="supervisor_license_number"
                    value={form?.supervisor_license_number || ""}
                    onChange={(e) => handleChange(e)}
                    label="Supervisor License Number"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-left text-19">Branch Address</label>
                  <InputField
                    className="bg-slate-300 px-4 py-2"
                    name="branch_address"
                    value={form?.branch_address || ""}
                    onChange={(e) => handleChange(e)}
                    label="Branch Address"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-left text-19">Branch Phone</label>
                  <InputField
                    className="bg-slate-300 px-4 py-2"
                    name="branch_phone"
                    value={form?.branch_phone || ""}
                    onChange={(e) => handleChange(e)}
                    label="Branch Phone"
                  />
                </div>
              </>
            )}
            <div className="space-y-4">
              <h3 className="text-left text-lg font-semibold text-slate-900">
                Social Media Links (7 Platforms)
              </h3>

              <div className="flex flex-col">
                <label className="flex items-center gap-2 text-left text-sm font-semibold text-slate-700 mb-2">
                  <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#4267B2]">
                    <SiFacebook size={16} className="text-white" />
                  </div>
                  Facebook URL
                </label>
                <input
                  className="bg-slate-300 px-4 py-2 rounded-lg border border-slate-400 focus:outline-none focus:ring-2 focus:ring-[#4267B2]"
                  name="facebook"
                  placeholder="facebook.com/yourpage or full URL"
                  value={form?.facebook || ""}
                  onChange={(e) => handleChange(e)}
                />
                {form?.facebook &&
                  !isValidSocialUrl(form?.facebook, "facebook") && (
                    <p className="text-red-600 text-xs mt-1">
                      ⚠️ Invalid URL format
                    </p>
                  )}
                {form?.facebook &&
                  isValidSocialUrl(form?.facebook, "facebook") && (
                    <p className="text-green-600 text-xs mt-1">✓ Valid URL</p>
                  )}
              </div>

              <div className="flex flex-col">
                <label className="flex items-center gap-2 text-left text-sm font-semibold text-slate-700 mb-2">
                  <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-r from-pink-500 to-purple-500">
                    <SiInstagram size={16} className="text-white" />
                  </div>
                  Instagram URL
                </label>
                <input
                  className="bg-slate-300 px-4 py-2 rounded-lg border border-slate-400 focus:outline-none focus:ring-2 focus:ring-pink-500"
                  name="instagram"
                  placeholder="instagram.com/yourprofile or full URL"
                  value={form?.instagram || ""}
                  onChange={(e) => handleChange(e)}
                />
                {form?.instagram &&
                  !isValidSocialUrl(form?.instagram, "instagram") && (
                    <p className="text-red-600 text-xs mt-1">
                      ⚠️ Invalid URL format
                    </p>
                  )}
                {form?.instagram &&
                  isValidSocialUrl(form?.instagram, "instagram") && (
                    <p className="text-green-600 text-xs mt-1">✓ Valid URL</p>
                  )}
              </div>

              <div className="flex flex-col">
                <label className="flex items-center gap-2 text-left text-sm font-semibold text-slate-700 mb-2">
                  <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-black">
                    <span className="font-bold text-white text-xs">𝕏</span>
                  </div>
                  X (Twitter) URL
                </label>
                <input
                  className="bg-slate-300 px-4 py-2 rounded-lg border border-slate-400 focus:outline-none focus:ring-2 focus:ring-black"
                  name="twitter"
                  placeholder="x.com/yourhandle or full URL"
                  value={form?.twitter || ""}
                  onChange={(e) => handleChange(e)}
                />
                {form?.twitter && !isValidSocialUrl(form?.twitter, "x") && (
                  <p className="text-red-600 text-xs mt-1">
                    ⚠️ Invalid URL format
                  </p>
                )}
                {form?.twitter && isValidSocialUrl(form?.twitter, "x") && (
                  <p className="text-green-600 text-xs mt-1">✓ Valid URL</p>
                )}
              </div>

              <div className="flex flex-col">
                <label className="flex items-center gap-2 text-left text-sm font-semibold text-slate-700 mb-2">
                  <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-black">
                    <SiTiktok size={16} className="text-white" />
                  </div>
                  TikTok URL
                </label>
                <input
                  className="bg-slate-300 px-4 py-2 rounded-lg border border-slate-400 focus:outline-none focus:ring-2 focus:ring-black"
                  name="tiktok"
                  placeholder="tiktok.com/@yourprofile or full URL"
                  value={form?.tiktok || ""}
                  onChange={(e) => handleChange(e)}
                />
                {form?.tiktok && !isValidSocialUrl(form?.tiktok, "tiktok") && (
                  <p className="text-red-600 text-xs mt-1">
                    ⚠️ Invalid URL format
                  </p>
                )}
                {form?.tiktok && isValidSocialUrl(form?.tiktok, "tiktok") && (
                  <p className="text-green-600 text-xs mt-1">✓ Valid URL</p>
                )}
              </div>

              <div className="flex flex-col">
                <label className="flex items-center gap-2 text-left text-sm font-semibold text-slate-700 mb-2">
                  <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#0A66C2]">
                    <SiLinkedin size={16} className="text-white" />
                  </div>
                  LinkedIn URL
                </label>
                <input
                  className="bg-slate-300 px-4 py-2 rounded-lg border border-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0A66C2]"
                  name="linkedin"
                  placeholder="linkedin.com/in/yourprofile or full URL"
                  value={form?.linkedin || ""}
                  onChange={(e) => handleChange(e)}
                />
                {form?.linkedin &&
                  !isValidSocialUrl(form?.linkedin, "linkedin") && (
                    <p className="text-red-600 text-xs mt-1">
                      ⚠️ Invalid URL format
                    </p>
                  )}
                {form?.linkedin &&
                  isValidSocialUrl(form?.linkedin, "linkedin") && (
                    <p className="text-green-600 text-xs mt-1">✓ Valid URL</p>
                  )}
              </div>

              <div className="flex flex-col">
                <label className="flex items-center gap-2 text-left text-sm font-semibold text-slate-700 mb-2">
                  <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#E60023]">
                    <SiPinterest size={16} className="text-white" />
                  </div>
                  Pinterest URL
                </label>
                <input
                  className="bg-slate-300 px-4 py-2 rounded-lg border border-slate-400 focus:outline-none focus:ring-2 focus:ring-[#E60023]"
                  name="pinterest"
                  placeholder="pinterest.com/yourprofile or full URL"
                  value={form?.pinterest || ""}
                  onChange={(e) => handleChange(e)}
                />
                {form?.pinterest &&
                  !isValidSocialUrl(form?.pinterest, "pinterest") && (
                    <p className="text-red-600 text-xs mt-1">
                      ⚠️ Invalid URL format
                    </p>
                  )}
                {form?.pinterest &&
                  isValidSocialUrl(form?.pinterest, "pinterest") && (
                    <p className="text-green-600 text-xs mt-1">✓ Valid URL</p>
                  )}
              </div>

              <div className="flex flex-col">
                <label className="flex items-center gap-2 text-left text-sm font-semibold text-slate-700 mb-2">
                  <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#FF0000]">
                    <SiYoutube size={16} className="text-white" />
                  </div>
                  YouTube URL
                </label>
                <input
                  className="bg-slate-300 px-4 py-2 rounded-lg border border-slate-400 focus:outline-none focus:ring-2 focus:ring-[#FF0000]"
                  name="youtube"
                  placeholder="youtube.com/@yourprofile or full URL"
                  value={form?.youtube || ""}
                  onChange={(e) => handleChange(e)}
                />
                {form?.youtube &&
                  !isValidSocialUrl(form?.youtube, "youtube") && (
                    <p className="text-red-600 text-xs mt-1">
                      ⚠️ Invalid URL format
                    </p>
                  )}
                {form?.youtube &&
                  isValidSocialUrl(form?.youtube, "youtube") && (
                    <p className="text-green-600 text-xs mt-1">✓ Valid URL</p>
                  )}
              </div>
            </div>
            {user?.class_type !== "" || user?.class_type !== "N/A" ? (
              <div className="flex flex-col bg-slate-50 border-2 border-slate-200 rounded-xl p-4 space-y-4">
                <div>
                  <label className="text-left text-lg font-semibold text-slate-900 mb-2 block">
                    Featured Videos (Up to 4)
                  </label>
                  <p className="text-left text-xs text-slate-600 mb-4">
                    Paste any valid URL to a video or profile page. Accepts
                    links from{" "}
                    <strong>
                      TikTok, Instagram, Facebook, LinkedIn, Pinterest, X
                      (Twitter), YouTube
                    </strong>
                    , or any other platform. Must be a valid public URL.
                  </p>
                </div>

                {/* Video 1 */}
                <div className="flex flex-col">
                  <label className="text-left text-sm font-semibold text-slate-700 mb-2">
                    Video 1 (Primary)
                  </label>
                  <input
                    className="bg-white px-4 py-2 rounded-lg border-2 border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    name="video"
                    placeholder="https://www.tiktok.com/@yourprofile or any valid URL"
                    value={form?.video || ""}
                    onChange={(e) => handleChange(e)}
                  />
                  {form?.video && !isSupportedVideoUrl(form?.video) && (
                    <p className="text-left text-xs text-red-600 mt-1 font-semibold">
                      ⚠️ Invalid URL format
                    </p>
                  )}
                  {form?.video && isSupportedVideoUrl(form?.video) && (
                    <p className="text-left text-xs text-green-600 mt-1 font-semibold">
                      ✓ Valid URL
                    </p>
                  )}
                </div>

                {/* Video 2 */}
                <div className="flex flex-col">
                  <label className="text-left text-sm font-semibold text-slate-700 mb-2">
                    Video 2 (Optional)
                  </label>
                  <input
                    className="bg-white px-4 py-2 rounded-lg border-2 border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    name="video2"
                    placeholder="https://www.tiktok.com/@yourprofile or any valid URL"
                    value={form?.video2 || ""}
                    onChange={(e) => handleChange(e)}
                  />
                  {form?.video2 && !isSupportedVideoUrl(form?.video2) && (
                    <p className="text-left text-xs text-red-600 mt-1 font-semibold">
                      ⚠️ Invalid URL format
                    </p>
                  )}
                  {form?.video2 && isSupportedVideoUrl(form?.video2) && (
                    <p className="text-left text-xs text-green-600 mt-1 font-semibold">
                      ✓ Valid URL
                    </p>
                  )}
                </div>

                {/* Video 3 */}
                <div className="flex flex-col">
                  <label className="text-left text-sm font-semibold text-slate-700 mb-2">
                    Video 3 (Optional)
                  </label>
                  <input
                    className="bg-white px-4 py-2 rounded-lg border-2 border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    name="video3"
                    placeholder="https://www.tiktok.com/@yourprofile or any valid URL"
                    value={form?.video3 || ""}
                    onChange={(e) => handleChange(e)}
                  />
                  {form?.video3 && !isSupportedVideoUrl(form?.video3) && (
                    <p className="text-left text-xs text-red-600 mt-1 font-semibold">
                      ⚠️ Invalid URL format
                    </p>
                  )}
                  {form?.video3 && isSupportedVideoUrl(form?.video3) && (
                    <p className="text-left text-xs text-green-600 mt-1 font-semibold">
                      ✓ Valid URL
                    </p>
                  )}
                </div>

                {/* Video 4 */}
                <div className="flex flex-col">
                  <label className="text-left text-sm font-semibold text-slate-700 mb-2">
                    Video 4 (Optional)
                  </label>
                  <input
                    className="bg-white px-4 py-2 rounded-lg border-2 border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    name="video4"
                    placeholder="https://www.tiktok.com/@yourprofile or any valid URL"
                    value={form?.video4 || ""}
                    onChange={(e) => handleChange(e)}
                  />
                  {form?.video4 && !isSupportedVideoUrl(form?.video4) && (
                    <p className="text-left text-xs text-red-600 mt-1 font-semibold">
                      ⚠️ Invalid URL format
                    </p>
                  )}
                  {form?.video4 && isSupportedVideoUrl(form?.video4) && (
                    <p className="text-left text-xs text-green-600 mt-1 font-semibold">
                      ✓ Valid URL
                    </p>
                  )}
                </div>
              </div>
            ) : (
              ""
            )}
            <div className="flex justify-end gap-3 mt-4 mb-2">
              <button
                className="px-4 py-2 rounded-10 border border-slate-400 text-slate-700 hover:bg-slate-100 transition-all"
                onClick={handleClose}
              >
                Cancel
              </button>
              <button
                className="bg-blue-500 text-white rounded-10 py-2 px-5 hover:scale-105 hover:cursor-pointer transition-all"
                onClick={() => handleUpdate()}
                disabled={!isDirty}
              >
                {isDirty ? "Save Changes" : "Saved"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ModalEdit;
